// src/components/ImageCanvasEditor.js
import React, { useRef, useState, useEffect } from "react";

/**
 * ImageCanvasEditor
 *
 * - Select images from device
 * - Tabs for each image
 * - Render selected image on base canvas
 * - Overlay canvas for edits (draw, erase, block erase, text)
 * - Save edited image as separate file or overwrite current image in memory
 *
 * No external libs required.
 */

const TOOLS = {
  PEN: "PEN",
  ERASER: "ERASER",
  BLOCK_ERASE: "BLOCK_ERASE",
  TEXT: "TEXT",
};

export default function ImageCanvasEditor() {
  const [images, setImages] = useState([]); // array of { name, dataUrl }
  const [index, setIndex] = useState(0); // selected image index
  const [tool, setTool] = useState(TOOLS.PEN);
  const [brushSize, setBrushSize] = useState(6);
  const [brushColor, setBrushColor] = useState("#111827");
  const [textValue, setTextValue] = useState("");
  const [textSize, setTextSize] = useState(20);

  const baseRef = useRef(null); // base canvas (image)
  const overlayRef = useRef(null); // overlay canvas (edits)
  const containerRef = useRef(null);

  const isDrawing = useRef(false);
  const lastPt = useRef({ x: 0, y: 0 });
  const blockStart = useRef(null);

  // handle file input (single or multiple)
  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const readers = files.map(
      (file) =>
        new Promise((resolve) => {
          const fr = new FileReader();
          fr.onload = () => resolve({ name: file.name, dataUrl: fr.result });
          fr.readAsDataURL(file);
        })
    );
    Promise.all(readers).then((arr) => {
      setImages((prev) => [...prev, ...arr]);
      setIndex(images.length); // select first newly added (append behavior)
    });
  };

  // render selected image onto base canvas
  const renderImage = (idx) => {
    const imgObj = images[idx];
    if (!imgObj) return;
    const base = baseRef.current;
    const overlay = overlayRef.current;
    const img = new Image();
    img.onload = () => {
      // set canvas to image natural resolution
      base.width = img.naturalWidth;
      base.height = img.naturalHeight;
      base.style.width = "100%";
      base.style.height = "auto";

      overlay.width = base.width;
      overlay.height = base.height;
      overlay.style.width = base.style.width;
      overlay.style.height = base.style.height;

      const ctx = base.getContext("2d");
      ctx.clearRect(0, 0, base.width, base.height);
      ctx.drawImage(img, 0, 0, base.width, base.height);

      // clear overlay when loading new image
      const octx = overlay.getContext("2d");
      octx.clearRect(0, 0, overlay.width, overlay.height);
    };
    img.src = imgObj.dataUrl;
  };

  // re-render when images or index change
  useEffect(() => {
    if (images.length > 0) {
      const i = Math.min(Math.max(0, index), images.length - 1);
      setIndex(i);
      renderImage(i);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images, index]);

  // convert mouse/touch event to canvas coords (handles responsive scaling)
  const getPos = (evt) => {
    const canvas = overlayRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = evt.touches ? evt.touches[0].clientX : evt.clientX;
    const clientY = evt.touches ? evt.touches[0].clientY : evt.clientY;
    return {
      x: ((clientX - rect.left) * canvas.width) / rect.width,
      y: ((clientY - rect.top) * canvas.height) / rect.height,
    };
  };

  // pointer handlers
  const handlePointerDown = (e) => {
    e.preventDefault();
    const pos = getPos(e);
    isDrawing.current = true;
    lastPt.current = pos;
    if (tool === TOOLS.BLOCK_ERASE) blockStart.current = pos;

    if (tool === TOOLS.PEN || tool === TOOLS.ERASER) {
      const ctx = overlayRef.current.getContext("2d");
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    }
  };

  const handlePointerMove = (e) => {
    if (!isDrawing.current) return;
    e.preventDefault();
    const pos = getPos(e);
    const ctx = overlayRef.current.getContext("2d");

    if (tool === TOOLS.PEN || tool === TOOLS.ERASER) {
      if (tool === TOOLS.ERASER) {
        ctx.globalCompositeOperation = "destination-out";
        ctx.lineWidth = Math.max(8, brushSize * 2);
      } else {
        ctx.globalCompositeOperation = "source-over";
        ctx.strokeStyle = brushColor;
        ctx.lineWidth = brushSize;
      }
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(lastPt.current.x, lastPt.current.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      lastPt.current = pos;
    } else {
      // for BLOCK_ERASE we could show a live rect preview (skipped for simplicity)
    }
  };

  const handlePointerUp = (e) => {
    if (!isDrawing.current) return;
    e.preventDefault();
    const pos = getPos(e);
    const ctx = overlayRef.current.getContext("2d");

    if (tool === TOOLS.BLOCK_ERASE && blockStart.current) {
      const start = blockStart.current;
      const x = Math.min(start.x, pos.x);
      const y = Math.min(start.y, pos.y);
      const w = Math.abs(pos.x - start.x);
      const h = Math.abs(pos.y - start.y);

      // paint white rectangle to visually remove base content
      ctx.save();
      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(x, y, w, h);
      ctx.restore();

      blockStart.current = null;
    } else if (tool === TOOLS.TEXT) {
      const txt = textValue.trim() || prompt("Enter text:");
      if (txt) {
        ctx.save();
        ctx.globalCompositeOperation = "source-over";
        ctx.font = `${textSize}px Arial`;
        ctx.fillStyle = brushColor;
        ctx.textBaseline = "top";
        ctx.fillText(txt, pos.x, pos.y);
        ctx.restore();
      }
    }

    isDrawing.current = false;
  };

  // touch event wiring (to allow mobile)
  useEffect(() => {
    const ov = overlayRef.current;
    if (!ov) return;
    const start = (ev) => handlePointerDown(ev);
    const move = (ev) => handlePointerMove(ev);
    const end = (ev) => handlePointerUp(ev);
    ov.addEventListener("touchstart", start, { passive: false });
    ov.addEventListener("touchmove", move, { passive: false });
    ov.addEventListener("touchend", end, { passive: false });
    return () => {
      ov.removeEventListener("touchstart", start);
      ov.removeEventListener("touchmove", move);
      ov.removeEventListener("touchend", end);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool, brushSize, brushColor, textValue, textSize]);

  // clear overlay edits (for current image)
  const clearEdits = () => {
    const o = overlayRef.current;
    o.getContext("2d").clearRect(0, 0, o.width, o.height);
  };

  // Composite base + overlay into a dataURL (PNG)
  const compositeToDataUrl = () => {
    const base = baseRef.current;
    const overlay = overlayRef.current;
    if (!base || !overlay) return null;
    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = base.width;
    exportCanvas.height = base.height;
    const ex = exportCanvas.getContext("2d");
    ex.drawImage(base, 0, 0);
    ex.drawImage(overlay, 0, 0);
    return exportCanvas.toDataURL("image/png");
  };

  // Save edited page as a new image (download + append to list)
  const saveAsNew = () => {
    const dataUrl = compositeToDataUrl();
    if (!dataUrl) return alert("Nothing to save.");
    // download
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `edited-image-${index + 1}.png`;
    a.click();
    // add to list (append)
    setImages((prev) => [...prev, { name: `edited-image-${prev.length + 1}.png`, dataUrl }]);
    // select new image
    setTimeout(() => setIndex(images.length), 50);
  };

  // Overwrite current image in memory (replace images[index])
  const overwriteCurrent = () => {
    const dataUrl = compositeToDataUrl();
    if (!dataUrl) return alert("Nothing to save.");
    setImages((prev) => {
      const copy = prev.slice();
      copy[index] = { name: copy[index].name, dataUrl };
      return copy;
    });
    alert("Current image overwritten in app memory. Download will not happen unless you click 'Download Overwritten Image'");
  };

  // download current image in images array (useful after overwrite)
  const downloadCurrent = () => {
    const img = images[index];
    if (!img) return;
    const a = document.createElement("a");
    a.href = img.dataUrl;
    a.download = img.name || `image-${index + 1}.png`;
    a.click();
  };

  return (
    <div style={{ maxWidth: 1000, margin: "14px auto", fontFamily: "sans-serif" }}>
      {/* <h3>ThinkStylus Form [Write & Draw Using ThinkStylus, Voice Input to Fill Form, Online Signature, AI Sentiment Analyser, Instant OCR Conversion and Storage for searchable, Collaborative Edit, Protect with Blockchain, Share]</h3> */}
    <div className="w-full rounded-2xl overflow-hidden shadow-xl mb-8">
    <div className="bg-gradient-to-r from-indigo-600 via-violet-600 to-pink-500 p-6 sm:p-8">
        <div className="flex items-start gap-4">
        <div className="flex-none">
            <div className="w-14 h-14 rounded-lg bg-white/20 flex items-center justify-center">
            <span className="text-white text-2xl">✍️</span>
            </div>
        </div>

        <div className="min-w-0 flex-1">
            <h3 className="text-white text-lg sm:text-2xl font-semibold leading-tight">
            ThinkStylus Form
            </h3>
            <p className="mt-1 text-indigo-100/90 text-sm sm:text-base">
            Write & draw with ThinkStylus — voice input, online signature, instant OCR, AI sentiment analysis,
            collaborative editing, blockchain protection and easy sharing.
            </p>

            {/* Feature badges */}
            <div className="mt-3 flex flex-wrap gap-2">
            {[
                "Write & Draw",
                "Voice Input",
                "Online Signature",
                "AI Sentiment",
                "Instant OCR",
                "Searchable Storage",
                "Collaborative Edit",
                "Blockchain Protect",
                "Share"
            ].map((b, idx) => (
                <span
                key={idx}
                className="text-xs sm:text-sm px-2 py-1 rounded-full bg-white/20 text-white/95 backdrop-blur-sm"
                >
                {b}
                </span>
            ))}
            </div>
        </div>

        <div className="flex-none flex items-center gap-2">
            <button
            className="hidden sm:inline-flex items-center gap-2 px-3 py-2 bg-white text-indigo-700 rounded-lg shadow-sm hover:scale-105 transition"
            aria-label="Start using ThinkStylus"
            >
            <strong>Get Started</strong>
            <span className="text-xs text-indigo-500">→</span>
            </button>
            <button
            className="inline-flex items-center gap-2 px-3 py-2 border border-white/25 text-white rounded-lg hover:bg-white/10 transition"
            aria-label="Learn more about ThinkStylus"
            >
            Learn
            </button>
        </div>
        </div>
    </div>

    {/* subtle footer / small extra line */}
    <div className="px-6 py-3 bg-white">
        <p className="text-gray-600 text-sm">
        Secure, mobile-friendly form experience — handwriting, voice and AI combined for smarter inputs.
        </p>
    </div>
    </div>
      <div style={{ marginBottom: 10, color: "white" }}>
        <input type="file" accept="image/*" multiple onChange={handleFiles} />
        <span style={{ marginLeft: 12, color: "white" }}>{images.length ? `Images: ${images.length}` : ""}</span>
      </div>

      {/* Tabs */}
      {images.length > 0 && (
        <div style={{ marginBottom: 12, display: "flex", gap: 6, flexWrap: "wrap" }}>
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              style={{
                padding: "6px 10px",
                borderRadius: 6,
                border: "1px solid #ccc",
                background: i === index ? "#1f6feb" : "#fff",
                color: i === index ? "#fff" : "#000",
                cursor: "pointer",
              }}
            >
              {img.name || `Image ${i + 1}`}
            </button>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div style={{ marginBottom: 12, display: "flex", gap: 8, alignItems: "center" }}>
        <button onClick={() => setTool(TOOLS.PEN)}>✏️ Pen</button>
        <button onClick={() => setTool(TOOLS.ERASER)}>🧽 Erase</button>
        <button onClick={() => setTool(TOOLS.BLOCK_ERASE)}>⤫ Block Erase</button>
        <button onClick={() => setTool(TOOLS.TEXT)}>🔤 Text</button>

        <label style={{ marginLeft: 8 }}>Brush</label>
        <input
          type="range"
          min="1"
          max="60"
          value={brushSize}
          onChange={(e) => setBrushSize(parseInt(e.target.value || "6", 10))}
        />
        <input type="color" value={brushColor} onChange={(e) => setBrushColor(e.target.value)} />

        {tool === TOOLS.TEXT && (
          <>
            <input
              type="text"
              placeholder="Default text (or you'll be prompted)"
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
              style={{ marginLeft: 8 }}
            />
            <input
              type="number"
              min="8"
              max="200"
              value={textSize}
              onChange={(e) => setTextSize(Math.max(8, parseInt(e.target.value || "20", 10)))}
              style={{ width: 80, marginLeft: 6 }}
            />
          </>
        )}

        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button onClick={clearEdits}>Clear Edits</button>
          <button onClick={saveAsNew} style={{ background: "#2563eb", color: "#fff" }}>
            Save As New
          </button>
          <button onClick={overwriteCurrent} style={{ background: "#059669", color: "#fff" }}>
            Overwrite Current (in memory)
          </button>
          <button onClick={downloadCurrent}>Download Current</button>
        </div>
      </div>

      {/* Canvas container */}
      <div ref={containerRef} style={{ position: "relative", border: "1px solid #ddd", borderRadius: 6 }}>
        <canvas ref={baseRef} style={{ display: "block", width: "100%" }} />
        <canvas
          ref={overlayRef}
          style={{ position: "absolute", left: 0, top: 0, width: "100%", height: "100%", touchAction: "none" }}
          onMouseDown={handlePointerDown}
          onMouseMove={handlePointerMove}
          onMouseUp={handlePointerUp}
          onMouseLeave={handlePointerUp}
        />
      </div>

      <div style={{ marginTop: 8, color: "#444", fontSize: 13 }}>
        <p style={{ margin: 0 }}>
          Notes: Pen draws freehand strokes on overlay; Eraser removes overlay strokes; Block Erase paints a white rectangle to hide base
          content (visual only); Text places text from input or prompt.
        </p>
      </div>
    </div>
  );
}
